import type { ReactElement } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";

/**
 * Studies-hub sidebar — ported from _prototype/components/studies/Sidebar.tsx
 * (architecture §5.3). Light-theme rail with the Leaply Research workspace
 * card on top, "Platform" group with accent-coloured icon tiles below, and
 * a user identity / sign-out footer above the "Leaply AI Studies Studio"
 * tagline.
 *
 * The accent colours (blue/emerald/violet) are decorative — they're NOT
 * semantic status colours (those stay reserved for valid/invalid/running
 * /archived on study + interview rows).
 */
export function AppShell() {
  const { user, signOut } = useAuth();
  return (
    <div className="fixed inset-0 z-30 flex bg-white text-neutral-900">
      <Sidebar
        userEmail={user?.email ?? ""}
        onSignOut={() => void signOut()}
      />
      <main className="flex-1 min-w-0 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: ReactElement;
  /** Tailwind classes for the icon tile — `accentBg accentText`. */
  accent: string;
  disabled?: boolean;
  badge?: string;
}

const NAV: NavItem[] = [
  {
    to: "/dashboard",
    label: "Dashboard",
    disabled: true,
    badge: "Soon",
    accent: "bg-neutral-100 text-neutral-400",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
        <rect x="3" y="3" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <rect x="13" y="3" width="8" height="5" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <rect x="13" y="10" width="8" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <rect x="3" y="13" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ),
  },
  {
    to: "/studies",
    label: "My Studies",
    accent: "bg-blue-100 text-blue-600",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
        <path
          d="M6 3h8l4 4v14l-4-3-4 3-4-3-4 3V3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    to: "/interviews",
    label: "Interviews database",
    accent: "bg-emerald-100 text-emerald-600",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
        <ellipse cx="12" cy="5" rx="8" ry="2.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ),
  },
  {
    to: "/prompts",
    label: "Prompts",
    accent: "bg-violet-100 text-violet-600",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
        <path
          d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-7l-4 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path d="M7 9h10M7 12h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
];

function Sidebar({
  userEmail,
  onSignOut,
}: {
  userEmail: string;
  onSignOut: () => void;
}) {
  const location = useLocation();
  return (
    <aside className="w-64 shrink-0 border-r border-neutral-200 bg-neutral-50 flex flex-col">
      {/* Workspace identity — Leaply Research, static (no switcher in v1).
          Avatar is the Leaply mark (matches the participant app + browser
          favicon); the rounded-full crop trims the icon's transparent edge
          so it sits flush in the rail. */}
      <div className="px-3 py-3 border-b border-neutral-200">
        <div className="flex items-center gap-2.5 rounded-lg px-2 py-2">
          <img
            src="/logomark.svg"
            alt="Leaply"
            width={36}
            height={36}
            className="w-9 h-9 shrink-0 rounded-full object-cover"
          />
          <span className="flex-1 min-w-0">
            <span className="block text-[13px] font-semibold text-neutral-900 truncate">
              Leaply Research
            </span>
          </span>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="px-2 mb-2 text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
          Platform
        </div>
        <ul className="space-y-0.5">
          {NAV.map((item) => {
            // Active state mirrors the prototype: exact match always wins;
            // "My Studies" also lights up on /studies/:id detail pages.
            const isActive =
              !item.disabled &&
              (location.pathname === item.to ||
                (item.to === "/studies" &&
                  location.pathname.startsWith("/studies/")));

            return (
              <li key={item.to}>
                {item.disabled ? (
                  <div
                    aria-disabled="true"
                    title={item.badge ? "Coming soon" : undefined}
                    className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-medium text-neutral-400 cursor-not-allowed select-none"
                  >
                    <IconTile accent={item.accent}>{item.icon}</IconTile>
                    <span className="flex-1">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto inline-flex items-center rounded-md bg-neutral-200 text-neutral-600 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5">
                        {item.badge}
                      </span>
                    )}
                  </div>
                ) : (
                  <NavLink
                    to={item.to}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-medium transition-colors ${
                      isActive
                        ? "bg-white shadow-sm border border-neutral-200 text-neutral-900"
                        : "text-neutral-700 hover:bg-white hover:text-neutral-900"
                    }`}
                  >
                    <IconTile accent={item.accent}>{item.icon}</IconTile>
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User identity + sign-out, then the "Studies Studio" tagline. */}
      <div className="px-3 py-3 border-t border-neutral-200">
        <div className="px-2 py-1">
          <p className="text-[12px] text-neutral-600 truncate" title={userEmail}>
            {userEmail}
          </p>
          <button
            type="button"
            onClick={onSignOut}
            className="mt-1 text-[12px] text-neutral-500 hover:text-neutral-900 hover:underline"
          >
            Sign out
          </button>
        </div>
      </div>
      <div className="px-4 py-3 border-t border-neutral-200 text-[11px] text-neutral-400">
        Leaply AI Studies Studio
      </div>
    </aside>
  );
}

function IconTile({
  accent,
  children,
}: {
  accent: string;
  children: ReactElement;
}) {
  return (
    <span
      className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md ${accent}`}
    >
      {children}
    </span>
  );
}
